import React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import CircularProgress from '@mui/joy/CircularProgress';
import Button from '@mui/joy/Button';
import ToggleButtonGroup from '@mui/joy/ToggleButtonGroup';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { getInventorySyncStatistics } from '../../inventorySyncApi';
import { OVERVIEW_HINTS } from '../../inventorySyncHints';
import { InventorySyncOverview, ServiceMetricsSnapshot } from '../../../../models/inventorySync';
import { ServiceMetricsPanel } from './ServiceMetricsPanel';
import { OverviewPanel } from './OverviewPanel';
import { HintLabel } from './shared';

const WINDOWS = [
    { minutes: 60, label: '60 мин' },
    { minutes: 1440, label: '24 ч' },
];

export function StatisticsSubTab(props: { isActive: boolean }) {
    const { isActive } = props;
    const [minutes, setMinutes] = React.useState(60);
    const [loading, setLoading] = React.useState(false);
    const [overview, setOverview] = React.useState<InventorySyncOverview | null>(null);
    const [metrics, setMetrics] = React.useState<ServiceMetricsSnapshot | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    // Источники, которые не загрузились — показанные значения неполные
    const [failedSources, setFailedSources] = React.useState<string[]>([]);
    const autoLoadedRef = React.useRef(false);
    // Ответ на устаревший запрос (окно переключили раньше, чем он пришёл) не показываем
    const requestIdRef = React.useRef(0);

    const loadStatistics = React.useCallback(async (windowMinutes: number) => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);
        setFailedSources([]);
        try {
            const result = await getInventorySyncStatistics(windowMinutes);
            if (requestIdRef.current !== requestId) {
                return;
            }
            if (result.result && result.value) {
                setOverview(result.value.overview ?? null);
                setMetrics(result.value.metrics ?? null);
                setFailedSources(result.value.failedSources);
            } else {
                setError('Не удалось загрузить статистику синка');
            }
        } catch {
            if (requestIdRef.current === requestId) {
                setError('Ошибка запроса статистики');
            }
        } finally {
            if (requestIdRef.current === requestId) {
                setLoading(false);
            }
        }
    }, []);

    React.useEffect(() => {
        if (isActive && !autoLoadedRef.current) {
            autoLoadedRef.current = true;
            void loadStatistics(minutes);
        }
    }, [isActive, loadStatistics, minutes]);

    const changeWindow = (value: number) => {
        setMinutes(value);
        void loadStatistics(value);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: { sm: 'flex-end' }, alignItems: { sm: 'center' } }}>
                <HintLabel label="Окно" hint={OVERVIEW_HINTS.window} level="body-sm" />
                <ToggleButtonGroup
                    size="sm"
                    value={String(minutes)}
                    disabled={loading}
                    onChange={(_, value) => value && changeWindow(Number(value))}
                >
                    {WINDOWS.map(w => <Button key={w.minutes} value={String(w.minutes)}>{w.label}</Button>)}
                </ToggleButtonGroup>
                <Button
                    size="sm"
                    variant="outlined"
                    startDecorator={<KeyboardArrowRightIcon />}
                    onClick={() => loadStatistics(minutes)}
                    disabled={loading}
                    loading={loading}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Обновить
                </Button>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size="sm" />
                    <Typography level="body-sm">Загрузка...</Typography>
                </Box>
            )}

            {!loading && error && (
                <Typography color="danger" level="body-md">{error}</Typography>
            )}

            {!loading && !error && failedSources.length > 0 && (
                <Typography color="warning" level="body-sm">
                    Не удалось загрузить: {failedSources.join(', ')}. Показаны данные только доступных источников.
                </Typography>
            )}

            {!loading && !error && overview && (
                <OverviewPanel overview={overview} />
            )}

            {!loading && !error && metrics && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ mt: 1 }}>
                        <HintLabel label="Счётчики процессов с момента старта" hint={OVERVIEW_HINTS.processMetrics} level="body-sm" />
                    </Box>
                    <ServiceMetricsPanel metrics={metrics} />
                </Box>
            )}
        </Box>
    );
}

export default StatisticsSubTab;
